import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReviewPage from '../../shared/booking/ReviewPage';
import { ExpiredSelection } from '../../shared/booking/FlowParts';
import { isInternational } from '../../shared/data/common';
import { CHILD_FACTOR, INFANT_FARE } from './data';
import FlightSummary from './FlightSummary';
import FlightAddons, { addonTotals, segmentsFromReview, servicesFor } from './FlightAddons';
import { checkoutFlight, reviewFlight } from '../../api/flights';
import { useAuth } from '../../auth/AuthContext';

const checkoutStatus = { Confirmed: 'confirmed', OnHold: 'held', Pending: 'pending' };

// TripJack accepts only Master or Ms for children and infants; "Miss" is shown but sent as Ms.
const youngTitles = { title: [['Master', 'Master'], ['Ms', 'Miss']] };

/** Turns "travellerInfo[1].title : Invalid Traveller title" into a message naming the traveller. */
function supplierMessage(message, specs) {
  const names = [...new Set([...(message || '').matchAll(/travellerInfo\[(\d+)\]\.(\w+)/g)]
    .map(([, i, field]) => specs[Number(i)] && `${specs[Number(i)].label} (${field === 'ti' || field === 'title' ? 'title' : field})`)
    .filter(Boolean))];
  return names.length
    ? `The airline didn't accept the details for ${names.join(', ')}. Please correct them and try again.`
    : message;
}

export default function FlightReview() {
  const { state } = useLocation();
  const { auth, signInWith } = useAuth();
  const [addons, setAddons] = useState({});
  // Replaces the reviewed booking id after a failed attempt; the supplier won't book a used id again.
  const [freshBookingId, setFreshBookingId] = useState(null);
  if (!state?.summary) return <ExpiredSelection module="flights" />;

  const { summary, backTo, liveOffer } = state;
  const adultFare = liveOffer ? liveOffer.totalPrice / Math.max(1, summary.pax.adults) : state.adultFare;
  const { adults, children, infants } = summary.pax;
  const intl = isInternational(summary.from, summary.to);
  // Date of birth (child/infant) and passport fields (international) are added by ReviewPage.
  const baseFields = ['title', 'firstName', 'lastName', 'gender'];
  const specs = [
    ...Array.from({ length: adults }, (_, i) => ({ type: 'Adult', label: `Adult ${i + 1}`, note: '12 yrs+', fields: baseFields })),
    ...Array.from({ length: children }, (_, i) => ({ type: 'Child', label: `Child ${i + 1}`, note: '2–12 yrs', fields: baseFields, options: youngTitles })),
    ...Array.from({ length: infants }, (_, i) => ({ type: 'Infant', label: `Infant ${i + 1}`, note: 'Below 2 yrs', fields: baseFields, options: youngTitles })),
  ];

  const childFare = Math.round(adultFare * CHILD_FACTOR);
  const base = adultFare * adults + childFare * children + INFANT_FARE * infants;
  const reviewData = liveOffer ? liveOffer.review?.data || liveOffer.review : null;
  const liveFare = reviewData?.totalPriceInfo?.totalFareDetail?.fC;
  const extras = addonTotals(addons);
  const extrasTotal = extras.seat + extras.meal + extras.baggage;
  const extraLines = [['Seats', extras.seat], ['Meals', extras.meal], ['Extra baggage', extras.baggage]].filter(([, amount]) => amount > 0);
  const lines = liveOffer
    ? [
      ...(liveFare?.BF != null && liveFare?.TAF != null
        ? [['Base fare', liveFare.BF], ['Taxes & airline fees', liveFare.TAF, 'tax']]
        : [['Airline fare', liveOffer.totalPrice]]),
      ...extraLines,
    ]
    : [
      [`Base fare — Adult × ${adults}`, adultFare * adults],
      ...(children ? [[`Base fare — Child × ${children}`, childFare * children]] : []),
      ...(infants ? [[`Infant fare × ${infants}`, INFANT_FARE * infants]] : []),
      ['Taxes & airline fees', Math.round(base * 0.18), 'tax'],
    ];

  const submitLiveBooking = liveOffer ? async ({ travellers, contact, fare, payment, promoCode, paymentId }) => {
    const bookingId = freshBookingId || reviewData?.bookingId;
    if (!bookingId) {
      throw new Error('The airline did not return a booking reference. Please select the fare again.');
    }

    // Re-reviews the fare so the next attempt books with a new booking id.
    const refreshBookingId = () => reviewFlight(liveOffer.supplierCode, liveOffer.offerReferences, auth?.accessToken)
      .then((review) => { const id = (review?.data || review)?.bookingId; if (id) setFreshBookingId(id); })
      .catch(() => {});

    let result;
    try {
      result = await checkoutFlight({
        supplierCode: liveOffer.supplierCode,
        bookingId,
        totalAmount: liveOffer.totalPrice + extrasTotal,
        currency: liveOffer.currency || 'INR',
        contact: { email: contact.email.trim(), phone: contact.phone.trim() },
        travellers: travellers.map((traveller, index) => ({
          paxType: traveller.type.toUpperCase(),
          title: traveller.title,
          firstName: traveller.firstName,
          lastName: traveller.lastName,
          dateOfBirth: traveller.dob || null,
          passportNumber: traveller.passport || null,
          passportExpiryDate: traveller.passportExpiry || null,
          passportNationality: traveller.nationality || null,
          selectedServices: servicesFor(addons, index),
        })),
        gst: contact.gst
          ? { gstNumber: contact.gstin, registeredName: contact.company, address: contact.gstAddress }
          : null,
        paymentMethod: payment,
        paymentId,
        promoCode,
        // The API stops before booking if its fee/discount no longer gives this total.
        expectedTotal: fare.total,
      }, auth?.accessToken);
    } catch (error) {
      // Supplier rejections (e.g. a title it won't take) come back as 502; price and promo
      // problems (400/409) are handled by ReviewPage and keep the same booking id.
      if (error.status === 502 || error.status === 503) {
        await refreshBookingId();
        error.message = supplierMessage(error.message, specs);
      }
      throw error;
    }

    if (result.status === 'Failed') {
      await refreshBookingId();
      throw new Error('The airline could not confirm this booking. Please choose another flight.');
    }
    if (result.auth) signInWith(result.auth);
    return {
      status: checkoutStatus[result.status] || 'pending',
      // Shown as "Airline PNR"; the supplier booking id is kept separately for live lookups.
      ref: [...new Set(Object.values(result.pnrs || {}))].join(', ') || 'Pending',
      supplierCode: liveOffer.supplierCode,
      supplierBookingId: result.supplierBookingId,
      pnrs: result.pnrs,
      account: { status: result.accountStatus, email: result.accountEmail, emailSent: result.emailSent },
      addons,
    };
  } : undefined;

  const contactNote = liveOffer && !auth?.accessToken && (
    <p className="muted small form-note account-note">
      Have a Trawtel account? <Link className="link small" to={`/account/login?returnTo=${encodeURIComponent(backTo)}`}>Sign in</Link> first,
      or we'll add this booking to the account for this email — and create one if it doesn't exist yet.
    </p>
  );

  return (
    <ReviewPage
      module="flights"
      heading="Review your flight"
      back={{ to: backTo, label: 'Change fare' }}
      summary={<FlightSummary summary={summary} />}
      info={[
        'Web check-in opens 48 hours before departure on the airline website.',
        intl ? 'Carry a passport valid for at least 6 months from the date of travel, plus a valid visa.' : 'Carry a valid government photo ID (Aadhaar, PAN, passport or driving licence).',
        `Reach the airport at least ${intl ? 3 : 2} hours before departure.`,
        `Baggage allowance: cabin ${summary.baggage.cabin}, check-in ${summary.baggage.checkin} per adult.`,
      ]}
      travellers={specs}
      international={intl}
      travelDate={summary.date}
      fareLines={lines}
      onSubmit={submitLiveBooking}
      submitNotice={liveOffer ? 'The ticket is booked with the airline on confirmation.' : undefined}
      requireGstAddress={Boolean(liveOffer)}
      initialContact={auth?.user?.email ? { email: auth.user.email } : undefined}
      contactNote={contactNote}
      afterTravellers={liveOffer && (
        <FlightAddons
          segments={segmentsFromReview(reviewData)}
          travellers={specs.map((spec, index) => ({ ...spec, index })).filter((spec) => spec.type !== 'Infant')}
          value={addons}
          onChange={setAddons}
          supplierCode={liveOffer.supplierCode}
          bookingId={reviewData?.bookingId}
          seatsApplicable={reviewData?.conditions?.isa !== false} />
      )}
      buildBooking={() => ({
        title: `${summary.from} → ${summary.to}`,
        subtitle: `${summary.airline.name} ${summary.flightNo} · ${adults + children + infants} traveller${adults + children + infants > 1 ? 's' : ''} · ${summary.fareName}`,
        travelDate: summary.date,
        summary,
      })}
    />
  );
}
